import React, { Children } from 'react';
export type BlogBlock = {
  type: 'paragraph';
  text: string;
} | {
  type: 'heading';
  text: string;
} | {
  type: 'subheading';
  text: string;
} | {
  type: 'list';
  items: string[];
};
export interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  author: string;
  image: string;
  featured: boolean;
  content: BlogBlock[];
}
export const BLOG_POSTS: BlogPost[] = [{
  id: 1,
  title: 'Understanding and Managing Diabetes',
  excerpt: 'Diabetes is a chronic condition that affects how your body turns food into energy. Learn about the types, symptoms, and effective management strategies.',
  category: 'Health & Wellness',
  date: 'June 2026',
  readTime: '8 min read',
  author: 'Maisha Medical Center',
  image: 'https://images.unsplash.com/photo-1505576399279-565b52d4ac71?auto=format&fit=crop&q=80&w=1200',
  featured: true,
  content: [{
    type: 'paragraph',
    text: 'Diabetes is one of the fastest-growing chronic health conditions worldwide, affecting millions of people and families. The good news is that with early diagnosis, proper treatment, and healthy lifestyle choices, people living with diabetes can lead long, active, and healthy lives.'
  }, {
    type: 'paragraph',
    text: 'At Maisha Medical Center, we are committed to helping our community understand diabetes, recognize its warning signs, and manage the condition effectively.'
  }, {
    type: 'heading',
    text: 'What is Diabetes?'
  }, {
    type: 'paragraph',
    text: "Diabetes is a chronic condition that occurs when your body is unable to properly regulate blood sugar (glucose). Glucose is the body's main source of energy, and insulin—a hormone produced by the pancreas—helps move glucose from the bloodstream into your cells."
  }, {
    type: 'paragraph',
    text: 'When insulin is not produced or used effectively, blood sugar levels rise, leading to diabetes.'
  }, {
    type: 'heading',
    text: 'Types of Diabetes'
  }, {
    type: 'subheading',
    text: 'Type 1 Diabetes'
  }, {
    type: 'paragraph',
    text: "Type 1 diabetes is an autoimmune condition where the body's immune system attacks the insulin-producing cells of the pancreas. It is commonly diagnosed in children and young adults but can occur at any age. People with Type 1 diabetes require lifelong insulin therapy."
  }, {
    type: 'subheading',
    text: 'Type 2 Diabetes'
  }, {
    type: 'paragraph',
    text: 'Type 2 diabetes is the most common form of diabetes. It develops when the body becomes resistant to insulin or does not produce enough insulin. Risk factors include excess weight, physical inactivity, family history, and advancing age. Healthy lifestyle changes and medication can help manage the condition.'
  }, {
    type: 'subheading',
    text: 'Gestational Diabetes'
  }, {
    type: 'paragraph',
    text: 'Gestational diabetes develops during pregnancy and usually resolves after childbirth. However, it increases the risk of developing Type 2 diabetes later in life for both mother and child. Regular antenatal care is essential for early detection and management.'
  }, {
    type: 'heading',
    text: 'Common Symptoms'
  }, {
    type: 'paragraph',
    text: 'Many people may not notice symptoms in the early stages. Common signs include:'
  }, {
    type: 'list',
    items: ['Frequent urination', 'Increased thirst', 'Increased hunger', 'Unexplained weight loss', 'Fatigue', 'Blurred vision', 'Slow-healing wounds', 'Frequent infections', 'Tingling or numbness in the hands and feet']
  }, {
    type: 'paragraph',
    text: 'If you experience any of these symptoms, consult a healthcare professional promptly.'
  }, {
    type: 'heading',
    text: 'Risk Factors'
  }, {
    type: 'paragraph',
    text: 'You may be at higher risk if you:'
  }, {
    type: 'list',
    items: ['Have a family history of diabetes', 'Are overweight or obese', 'Have high blood pressure', 'Have high cholesterol', 'Lead a physically inactive lifestyle', 'Are over the age of 45', 'Had gestational diabetes during pregnancy']
  }, {
    type: 'heading',
    text: 'How is Diabetes Diagnosed?'
  }, {
    type: 'paragraph',
    text: 'Diabetes can be diagnosed through simple laboratory tests, including:'
  }, {
    type: 'list',
    items: ['Fasting Blood Sugar Test', 'Random Blood Sugar Test', 'HbA1c Test', 'Oral Glucose Tolerance Test (OGTT)']
  }, {
    type: 'paragraph',
    text: 'Early testing helps identify diabetes before serious complications develop.'
  }, {
    type: 'heading',
    text: 'Managing Diabetes'
  }, {
    type: 'paragraph',
    text: 'Successful diabetes management involves a combination of healthy lifestyle choices and medical care.'
  }, {
    type: 'subheading',
    text: 'Healthy Eating'
  }, {
    type: 'paragraph',
    text: 'Choose a balanced diet that includes:'
  }, {
    type: 'list',
    items: ['Whole grains', 'Fresh vegetables', 'Fruits in moderation', 'Lean proteins', 'Healthy fats', 'Plenty of water']
  }, {
    type: 'paragraph',
    text: 'Limit sugary drinks, sweets, highly processed foods, excess salt, and saturated fats.'
  }, {
    type: 'subheading',
    text: 'Stay Physically Active'
  }, {
    type: 'paragraph',
    text: 'Regular exercise helps lower blood sugar and improves overall health. Aim for at least 150 minutes of moderate physical activity each week, such as:'
  }, {
    type: 'list',
    items: ['Walking', 'Cycling', 'Swimming', 'Dancing', 'Light jogging']
  }, {
    type: 'paragraph',
    text: 'Always consult your healthcare provider before starting a new exercise program.'
  }, {
    type: 'subheading',
    text: 'Monitor Your Blood Sugar'
  }, {
    type: 'paragraph',
    text: "Regular blood sugar monitoring helps you understand how food, activity, medication, and stress affect your glucose levels. Follow your healthcare provider's recommendations for testing frequency."
  }, {
    type: 'subheading',
    text: 'Take Medication as Prescribed'
  }, {
    type: 'paragraph',
    text: 'Some people manage diabetes through lifestyle changes alone, while others require tablets, insulin, or other medications. Never stop taking prescribed medication without consulting your healthcare provider.'
  }, {
    type: 'heading',
    text: 'Preventing Diabetes'
  }, {
    type: 'paragraph',
    text: 'Although Type 1 diabetes cannot currently be prevented, many cases of Type 2 diabetes can be delayed or prevented through healthy habits:'
  }, {
    type: 'list',
    items: ['Maintain a healthy weight', 'Eat nutritious meals', 'Exercise regularly', 'Avoid smoking', 'Limit alcohol consumption', 'Attend regular health check-ups']
  }, {
    type: 'paragraph',
    text: 'Small lifestyle changes today can make a significant difference in your future health.'
  }, {
    type: 'heading',
    text: 'Possible Complications'
  }, {
    type: 'paragraph',
    text: 'Poorly controlled diabetes can increase the risk of:'
  }, {
    type: 'list',
    items: ['Heart disease', 'Stroke', 'Kidney disease', 'Vision loss', 'Nerve damage', 'Foot ulcers', 'Poor wound healing']
  }, {
    type: 'paragraph',
    text: 'Routine medical reviews and good blood sugar control greatly reduce these risks.'
  }, {
    type: 'heading',
    text: 'Diabetes Care at Maisha Medical Center'
  }, {
    type: 'paragraph',
    text: 'Our healthcare team provides comprehensive diabetes care, including:'
  }, {
    type: 'list',
    items: ['Diabetes screening', 'Diagnosis and laboratory testing', 'Chronic disease management', 'Medication monitoring', 'Lifestyle and nutrition guidance', 'Blood pressure monitoring', 'Patient education and counselling', 'Ongoing follow-up care']
  }, {
    type: 'paragraph',
    text: 'We work closely with every patient to develop an individualized care plan that supports long-term health and wellbeing.'
  }, {
    type: 'heading',
    text: 'When Should You See a Doctor?'
  }, {
    type: 'paragraph',
    text: 'Schedule a consultation if you:'
  }, {
    type: 'list',
    items: ['Have symptoms of diabetes', 'Have a family history of diabetes', 'Are pregnant', 'Have high blood pressure or high cholesterol', 'Are overweight', 'Need help managing your diabetes']
  }, {
    type: 'paragraph',
    text: 'Early diagnosis and treatment can prevent serious complications and improve your quality of life.'
  }, {
    type: 'heading',
    text: 'Book an Appointment'
  }, {
    type: 'paragraph',
    text: 'If you are concerned about your blood sugar or would like to schedule a diabetes screening, our team is here to help. Maisha Medical Center offers compassionate, patient-centered care with modern diagnostic services and ongoing support for people living with diabetes.'
  }]
}, {
  id: 2,
  title: 'The Silent Killer: Managing Hypertension',
  excerpt: 'High blood pressure often has no symptoms but can lead to severe health complications. Discover how to monitor and control your blood pressure effectively.',
  category: 'Heart Health',
  date: 'June 2026',
  readTime: '8 min read',
  author: 'Maisha Medical Center',
  image: 'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?auto=format&fit=crop&q=80&w=1200',
  featured: false,
  content: [{
    type: 'paragraph',
    text: 'High blood pressure, also known as hypertension, is often called the "silent killer" because it usually develops without noticeable symptoms. Many people live with high blood pressure for years without realizing it, yet it can quietly damage the heart, brain, kidneys, and blood vessels, leading to life-threatening complications.'
  }, {
    type: 'paragraph',
    text: 'The encouraging news is that hypertension can be prevented, detected early, and effectively managed through regular health check-ups, healthy lifestyle choices, and appropriate medical care.'
  }, {
    type: 'paragraph',
    text: 'At Maisha Medical Center, we are dedicated to helping our community understand hypertension and take proactive steps toward better heart health.'
  }, {
    type: 'heading',
    text: 'What is Hypertension?'
  }, {
    type: 'paragraph',
    text: 'Blood pressure is the force of blood pushing against the walls of your arteries as your heart pumps. When this pressure remains consistently higher than normal, it is known as hypertension.'
  }, {
    type: 'paragraph',
    text: 'Over time, uncontrolled high blood pressure places extra strain on your heart and blood vessels, increasing the risk of serious health conditions.'
  }, {
    type: 'heading',
    text: 'Why is Hypertension Called the Silent Killer?'
  }, {
    type: 'paragraph',
    text: 'Unlike many illnesses, hypertension often causes no warning signs or symptoms in its early stages. Many people only discover they have high blood pressure during a routine medical examination or after experiencing a serious complication.'
  }, {
    type: 'paragraph',
    text: 'This is why regular blood pressure screening is one of the most important steps you can take to protect your health.'
  }, {
    type: 'heading',
    text: 'Risk Factors'
  }, {
    type: 'paragraph',
    text: 'Anyone can develop hypertension, but certain factors increase the risk, including:'
  }, {
    type: 'list',
    items: ['Family history of high blood pressure', 'Being overweight or obese', 'Physical inactivity', 'Excessive salt intake', 'Smoking', 'Excessive alcohol consumption', 'High cholesterol', 'Diabetes', 'Chronic stress', 'Advancing age']
  }, {
    type: 'paragraph',
    text: 'Understanding your risk factors allows you to make informed lifestyle choices and seek regular health screenings.'
  }, {
    type: 'heading',
    text: 'Symptoms'
  }, {
    type: 'paragraph',
    text: 'Although hypertension often has no symptoms, severely elevated blood pressure may cause:'
  }, {
    type: 'list',
    items: ['Persistent headaches', 'Dizziness', 'Blurred vision', 'Chest pain', 'Shortness of breath', 'Nosebleeds', 'Fatigue']
  }, {
    type: 'paragraph',
    text: 'If you experience any of these symptoms, especially chest pain or difficulty breathing, seek medical attention immediately.'
  }, {
    type: 'heading',
    text: 'Why Blood Pressure Checks Matter'
  }, {
    type: 'paragraph',
    text: 'Routine blood pressure checks can detect hypertension before complications occur. Regular monitoring helps:'
  }, {
    type: 'list',
    items: ['Identify high blood pressure early', 'Track treatment progress', 'Reduce the risk of heart disease and stroke', 'Improve long-term health outcomes']
  }, {
    type: 'paragraph',
    text: 'Even if you feel healthy, checking your blood pressure regularly is an important part of preventive healthcare.'
  }, {
    type: 'heading',
    text: 'Complications of Uncontrolled Hypertension'
  }, {
    type: 'paragraph',
    text: 'Without proper treatment, hypertension can lead to serious complications, including:'
  }, {
    type: 'list',
    items: ['Heart attack', 'Stroke', 'Heart failure', 'Kidney disease', 'Vision problems', 'Peripheral artery disease', 'Cognitive decline']
  }, {
    type: 'paragraph',
    text: 'Fortunately, many of these complications can be prevented through early diagnosis and effective management.'
  }, {
    type: 'heading',
    text: 'Managing Hypertension'
  }, {
    type: 'subheading',
    text: 'Eat a Heart-Healthy Diet'
  }, {
    type: 'paragraph',
    text: 'Healthy eating plays a vital role in controlling blood pressure. Aim to include:'
  }, {
    type: 'list',
    items: ['Fresh fruits and vegetables', 'Whole grains', 'Lean proteins', 'Low-fat dairy products', 'Healthy fats', 'Plenty of water']
  }, {
    type: 'paragraph',
    text: 'Reduce your intake of salt, processed foods, sugary beverages, saturated fats, and fried foods.'
  }, {
    type: 'subheading',
    text: 'Stay Physically Active'
  }, {
    type: 'paragraph',
    text: 'Regular exercise strengthens the heart and improves circulation. Aim for at least 150 minutes of moderate physical activity each week, such as:'
  }, {
    type: 'list',
    items: ['Brisk walking', 'Cycling', 'Swimming', 'Dancing', 'Light jogging']
  }, {
    type: 'paragraph',
    text: 'Always consult your healthcare provider before starting a new exercise program, especially if you have existing medical conditions.'
  }, {
    type: 'subheading',
    text: 'Maintain a Healthy Weight'
  }, {
    type: 'paragraph',
    text: 'Even modest weight loss can significantly lower blood pressure and reduce strain on the heart. A healthy weight also lowers the risk of diabetes and other chronic illnesses.'
  }, {
    type: 'subheading',
    text: 'Limit Alcohol and Avoid Smoking'
  }, {
    type: 'paragraph',
    text: 'Smoking damages blood vessels and greatly increases the risk of heart disease and stroke. Limiting alcohol consumption and quitting smoking are among the most effective ways to improve cardiovascular health.'
  }, {
    type: 'subheading',
    text: 'Manage Stress'
  }, {
    type: 'paragraph',
    text: 'Chronic stress can contribute to elevated blood pressure. Healthy ways to manage stress include:'
  }, {
    type: 'list',
    items: ['Regular exercise', 'Meditation', 'Deep breathing exercises', 'Spending time with family and friends', 'Getting enough sleep', 'Maintaining a healthy work-life balance']
  }, {
    type: 'subheading',
    text: 'Take Medication as Prescribed'
  }, {
    type: 'paragraph',
    text: 'Some individuals require medication to help control blood pressure. If medication is prescribed:'
  }, {
    type: 'list',
    items: ['Take it exactly as directed.', 'Never stop treatment without consulting your healthcare provider.', 'Attend regular follow-up appointments to monitor your progress.']
  }, {
    type: 'paragraph',
    text: 'Medication works best when combined with healthy lifestyle choices.'
  }, {
    type: 'heading',
    text: 'Preventing Hypertension'
  }, {
    type: 'paragraph',
    text: 'Many cases of high blood pressure can be prevented by:'
  }, {
    type: 'list',
    items: ['Eating a balanced diet', 'Exercising regularly', 'Maintaining a healthy weight', 'Reducing salt intake', 'Avoiding tobacco products', 'Limiting alcohol', 'Managing stress', 'Having routine medical check-ups']
  }, {
    type: 'paragraph',
    text: 'Prevention is one of the most effective ways to protect your heart and overall health.'
  }, {
    type: 'heading',
    text: 'Hypertension Care at Maisha Medical Center'
  }, {
    type: 'paragraph',
    text: 'Our experienced healthcare team provides comprehensive services to help patients prevent and manage hypertension, including:'
  }, {
    type: 'list',
    items: ['Blood pressure screening', 'Comprehensive medical consultations', 'Laboratory investigations', 'Chronic disease management', 'Medication reviews', 'Lifestyle and nutrition counselling', 'Ongoing monitoring and follow-up care', 'Patient education and support']
  }, {
    type: 'paragraph',
    text: 'We partner with our patients to create personalized care plans that promote healthier living and reduce the risk of complications.'
  }, {
    type: 'heading',
    text: 'When Should You See a Doctor?'
  }, {
    type: 'paragraph',
    text: 'Book a consultation if you:'
  }, {
    type: 'list',
    items: ['Have consistently high blood pressure readings', 'Experience symptoms such as headaches, dizziness, or blurred vision', 'Have a family history of hypertension', 'Have diabetes or kidney disease', 'Are overweight', 'Are over the age of 40 and have not had a recent blood pressure check']
  }, {
    type: 'paragraph',
    text: 'Regular health screenings can detect hypertension early, even before symptoms appear.'
  }, {
    type: 'heading',
    text: 'Take Control of Your Heart Health'
  }, {
    type: 'paragraph',
    text: 'Hypertension may be silent, but its effects can be serious if left untreated. The good news is that early detection, healthy lifestyle choices, and ongoing medical care can significantly reduce your risk of heart disease, stroke, and other complications.'
  }, {
    type: 'paragraph',
    text: 'If it has been a while since your last blood pressure check, now is the perfect time to schedule a visit. Book your appointment with Maisha Medical Center today and take the first step toward protecting your heart and your future.'
  }]
}, {
  id: 3,
  title: 'Essential Maternal Health Guidelines',
  excerpt: 'Pregnancy is a life-changing journey that requires regular medical care, proper nutrition, and a healthy lifestyle. Learn how to protect both mother and baby at every stage.',
  category: "Maternal & Women's Health",
  date: 'June 2026',
  readTime: '9 min read',
  author: 'Maisha Medical Center',
  image: 'https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&q=80&w=1200',
  featured: false,
  content: [{
    type: 'paragraph',
    text: 'Pregnancy is an exciting and life-changing journey that requires regular medical care, proper nutrition, and a healthy lifestyle to ensure the wellbeing of both mother and baby. Good maternal healthcare begins even before birth and continues through pregnancy, delivery, and the postnatal period.'
  }, {
    type: 'paragraph',
    text: 'At Maisha Medical Center, we are committed to supporting expectant mothers with compassionate, professional care at every stage of their pregnancy journey.'
  }, {
    type: 'heading',
    text: 'What is Maternal Health?'
  }, {
    type: 'paragraph',
    text: 'Maternal health refers to the healthcare provided to women during pregnancy, childbirth, and after delivery. Quality maternal care helps reduce health risks, supports healthy pregnancies, and gives every baby the best possible start in life.'
  }, {
    type: 'paragraph',
    text: 'Regular antenatal and postnatal visits allow healthcare providers to monitor both mother and baby, identify potential complications early, and provide guidance throughout pregnancy.'
  }, {
    type: 'heading',
    text: 'Why Antenatal Care is Important'
  }, {
    type: 'paragraph',
    text: "Antenatal care (ANC) is one of the most important aspects of a healthy pregnancy. Regular check-ups allow your healthcare provider to monitor your baby's growth and your overall health."
  }, {
    type: 'paragraph',
    text: 'During antenatal visits, your healthcare provider may:'
  }, {
    type: 'list',
    items: ['Monitor your blood pressure', "Check your baby's growth and development", 'Perform routine laboratory tests', 'Monitor your weight and overall health', 'Provide nutritional advice', 'Administer recommended immunizations', 'Screen for pregnancy-related complications', 'Offer guidance on preparing for delivery']
  }, {
    type: 'paragraph',
    text: 'Early and consistent antenatal care significantly improves outcomes for both mother and baby.'
  }, {
    type: 'heading',
    text: 'Healthy Eating During Pregnancy'
  }, {
    type: 'paragraph',
    text: "Proper nutrition supports your baby's growth while helping you stay healthy throughout pregnancy."
  }, {
    type: 'subheading',
    text: 'Aim to include:'
  }, {
    type: 'list',
    items: ['Fresh fruits and vegetables', 'Whole grains', 'Lean proteins', 'Dairy products rich in calcium', 'Healthy fats', 'Plenty of clean drinking water']
  }, {
    type: 'paragraph',
    text: 'Foods rich in iron, calcium, folic acid, and protein are especially important during pregnancy.'
  }, {
    type: 'subheading',
    text: 'Limit:'
  }, {
    type: 'list',
    items: ['Highly processed foods', 'Excess sugar', 'Excess salt', 'Sugary drinks', 'Alcohol']
  }, {
    type: 'paragraph',
    text: 'Always discuss nutritional supplements with your healthcare provider before taking them.'
  }, {
    type: 'heading',
    text: 'Stay Active Safely'
  }, {
    type: 'paragraph',
    text: 'Unless advised otherwise by your healthcare provider, moderate physical activity can provide many benefits during pregnancy.'
  }, {
    type: 'subheading',
    text: 'Safe activities include:'
  }, {
    type: 'list',
    items: ['Walking', 'Prenatal stretching', 'Swimming', 'Pregnancy-approved exercise classes']
  }, {
    type: 'subheading',
    text: 'Exercise may help:'
  }, {
    type: 'list',
    items: ['Improve circulation', 'Reduce back pain', 'Improve sleep', 'Increase energy', 'Prepare your body for labour']
  }, {
    type: 'paragraph',
    text: 'Always seek medical advice before starting or continuing an exercise routine during pregnancy.'
  }, {
    type: 'heading',
    text: 'Attend Every Scheduled Appointment'
  }, {
    type: 'paragraph',
    text: 'Every antenatal visit plays an important role in monitoring your pregnancy. These appointments help detect conditions such as:'
  }, {
    type: 'list',
    items: ['High blood pressure', 'Gestational diabetes', 'Anaemia', 'Infections', 'Poor fetal growth']
  }, {
    type: 'paragraph',
    text: 'Even if you feel well, do not skip your scheduled check-ups.'
  }, {
    type: 'heading',
    text: 'Warning Signs During Pregnancy'
  }, {
    type: 'paragraph',
    text: 'Seek immediate medical attention if you experience:'
  }, {
    type: 'list',
    items: ['Heavy bleeding', 'Severe abdominal pain', 'Persistent severe headaches', 'Blurred vision', 'Swelling of the face or hands', 'Reduced baby movements', 'High fever', 'Leaking fluid before labour', 'Difficulty breathing']
  }, {
    type: 'paragraph',
    text: 'Prompt medical care can protect both mother and baby.'
  }, {
    type: 'heading',
    text: 'Preparing for Delivery'
  }, {
    type: 'paragraph',
    text: 'As your due date approaches, it is important to prepare for labour and delivery. Plan ahead by:'
  }, {
    type: 'list',
    items: ['Packing a hospital bag', 'Arranging transportation', 'Keeping emergency contact numbers available', 'Attending childbirth education if available', 'Discussing your birth plan with your healthcare provider']
  }, {
    type: 'paragraph',
    text: 'Being prepared helps reduce stress and ensures a smoother delivery experience.'
  }, {
    type: 'heading',
    text: 'Postnatal Care Matters Too'
  }, {
    type: 'paragraph',
    text: 'Healthcare continues after your baby is born. Postnatal care helps monitor recovery and supports both mother and newborn during the first weeks after delivery.'
  }, {
    type: 'subheading',
    text: 'Postnatal visits include:'
  }, {
    type: 'list',
    items: ['Physical recovery assessment', 'Blood pressure monitoring', 'Breastfeeding support', 'Family planning counselling', 'Newborn health checks', 'Emotional wellbeing support']
  }, {
    type: 'paragraph',
    text: 'These appointments are essential for identifying and managing any complications early.'
  }, {
    type: 'heading',
    text: 'Caring for Your Newborn'
  }, {
    type: 'paragraph',
    text: "Your baby's health is closely linked to your own wellbeing. Important newborn care includes:"
  }, {
    type: 'list',
    items: ['Breastfeeding support', 'Routine immunizations', 'Regular growth monitoring', 'Safe sleeping practices', 'Good hygiene', 'Prompt medical attention if your baby becomes unwell']
  }, {
    type: 'paragraph',
    text: 'Our healthcare team is available to guide parents through every stage of newborn care.'
  }, {
    type: 'heading',
    text: 'Looking After Your Emotional Wellbeing'
  }, {
    type: 'paragraph',
    text: 'Pregnancy and motherhood bring many emotional changes. It is normal to experience a range of feelings, but prolonged sadness, anxiety, or feelings of being overwhelmed should not be ignored.'
  }, {
    type: 'paragraph',
    text: 'Speak to your healthcare provider if you experience:'
  }, {
    type: 'list',
    items: ['Persistent sadness', 'Difficulty bonding with your baby', 'Severe anxiety', 'Feelings of hopelessness', 'Difficulty sleeping unrelated to newborn care']
  }, {
    type: 'paragraph',
    text: 'Early support can make a significant difference to both mother and baby.'
  }, {
    type: 'heading',
    text: 'Maternal Healthcare at Maisha Medical Center'
  }, {
    type: 'paragraph',
    text: 'We provide comprehensive maternal healthcare services, including:'
  }, {
    type: 'list',
    items: ['Antenatal care', 'Postnatal care', 'Family planning services', 'Pregnancy monitoring', 'Routine laboratory investigations', 'Ultrasound services', 'Health education', 'Counselling and support', 'Referral to specialist care when required']
  }, {
    type: 'paragraph',
    text: 'Our goal is to ensure every mother receives safe, compassionate, and personalized care throughout pregnancy and beyond.'
  }, {
    type: 'heading',
    text: 'When Should You Book Your First Antenatal Visit?'
  }, {
    type: 'paragraph',
    text: 'You should schedule your first antenatal appointment as soon as you know you are pregnant, ideally within the first 12 weeks of pregnancy. Early care allows your healthcare provider to:'
  }, {
    type: 'list',
    items: ['Confirm your pregnancy', 'Estimate your due date', 'Identify any health concerns', 'Begin essential screenings', 'Provide guidance for a healthy pregnancy']
  }, {
    type: 'paragraph',
    text: 'Starting antenatal care early gives you and your baby the best possible foundation for a healthy pregnancy.'
  }, {
    type: 'heading',
    text: 'Begin Your Pregnancy Journey with Confidence'
  }, {
    type: 'paragraph',
    text: 'Every pregnancy is unique, and every mother deserves compassionate, high-quality healthcare. Regular antenatal visits, healthy lifestyle choices, and ongoing medical support are key to protecting both you and your baby.'
  }, {
    type: 'paragraph',
    text: 'At Maisha Medical Center, we are honoured to be part of your journey from pregnancy through motherhood. Book your antenatal appointment today and let our experienced healthcare team support you every step of the way.'
  }]
}, {
  id: 4,
  title: 'Child Wellness: Understanding Vaccination Schedules',
  excerpt: 'Vaccinations are one of the most effective ways to protect children from serious and potentially life-threatening diseases. Here is what every parent needs to know.',
  category: 'Child Health & Wellness',
  date: 'June 2026',
  readTime: '8 min read',
  author: 'Maisha Medical Center',
  image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&q=80&w=1200',
  featured: false,
  content: [{
    type: 'paragraph',
    text: 'Vaccinations are one of the most effective ways to protect children from serious and potentially life-threatening diseases. By following the recommended immunization schedule, parents can help ensure their children grow up healthy, protected, and ready to thrive.'
  }, {
    type: 'paragraph',
    text: "At Maisha Medical Center, we believe that prevention is the foundation of lifelong health. Our child wellness services include routine immunizations, developmental monitoring, health education, and preventive care to support your child's healthy growth from infancy through adolescence."
  }, {
    type: 'heading',
    text: 'Why Are Vaccinations Important?'
  }, {
    type: 'paragraph',
    text: "Vaccines help your child's immune system recognize and fight harmful viruses and bacteria before they can cause serious illness. Immunization not only protects your child but also helps safeguard the wider community by reducing the spread of infectious diseases."
  }, {
    type: 'paragraph',
    text: 'Vaccines have significantly reduced illnesses such as:'
  }, {
    type: 'list',
    items: ['Measles', 'Polio', 'Tuberculosis (TB)', 'Diphtheria', 'Tetanus', 'Whooping Cough (Pertussis)', 'Hepatitis B', 'Pneumococcal Disease', 'Rotavirus', 'Rubella']
  }, {
    type: 'paragraph',
    text: 'Routine immunization is one of the safest and most effective public health measures available.'
  }, {
    type: 'heading',
    text: 'When Should Vaccinations Begin?'
  }, {
    type: 'paragraph',
    text: 'Vaccinations begin shortly after birth and continue throughout childhood according to the national immunization schedule. Keeping up with each scheduled vaccination ensures your child develops strong protection at the right stages of growth.'
  }, {
    type: 'paragraph',
    text: "Always bring your child's health or vaccination card to every clinic visit so healthcare providers can update their immunization record."
  }, {
    type: 'heading',
    text: 'Typical Childhood Vaccination Timeline'
  }, {
    type: 'paragraph',
    text: 'While the exact schedule may vary based on national health guidelines, children are commonly vaccinated at the following ages:'
  }, {
    type: 'subheading',
    text: 'At Birth'
  }, {
    type: 'list',
    items: ['BCG (Tuberculosis)', 'Hepatitis B (where applicable)']
  }, {
    type: 'subheading',
    text: '6 Weeks'
  }, {
    type: 'list',
    items: ['Polio vaccine', 'Diphtheria, Tetanus and Pertussis (DTP)', 'Haemophilus influenzae type b (Hib)', 'Hepatitis B', 'Pneumococcal vaccine', 'Rotavirus vaccine']
  }, {
    type: 'subheading',
    text: '10 Weeks'
  }, {
    type: 'list',
    items: ['Second doses of routine childhood vaccines']
  }, {
    type: 'subheading',
    text: '14 Weeks'
  }, {
    type: 'list',
    items: ['Third doses of routine childhood vaccines', 'Final Rotavirus dose (where applicable)']
  }, {
    type: 'subheading',
    text: '9 Months'
  }, {
    type: 'list',
    items: ['Measles vaccine', 'Rubella vaccine (depending on national schedule)']
  }, {
    type: 'subheading',
    text: '18 Months and Beyond'
  }, {
    type: 'list',
    items: ['Booster vaccinations as recommended']
  }, {
    type: 'paragraph',
    text: "Your healthcare provider will advise you on the appropriate schedule based on Botswana's national immunization programme."
  }, {
    type: 'heading',
    text: 'What Happens During a Vaccination Visit?'
  }, {
    type: 'paragraph',
    text: 'During your child\u2019s appointment, healthcare professionals will:'
  }, {
    type: 'list',
    items: ["Review your child's health and vaccination record", 'Assess growth and development', 'Administer the recommended vaccines', 'Discuss any questions or concerns you may have', 'Provide guidance on possible mild side effects', 'Schedule the next appointment']
  }, {
    type: 'paragraph',
    text: "Vaccination appointments are also an excellent opportunity to discuss your child's nutrition, development, sleep, and general health."
  }, {
    type: 'heading',
    text: 'Are Vaccines Safe?'
  }, {
    type: 'paragraph',
    text: 'Yes. Vaccines undergo extensive research, testing, and continuous monitoring to ensure they are safe and effective. Most children experience only mild side effects, which may include:'
  }, {
    type: 'list',
    items: ['Mild fever', 'Temporary soreness at the injection site', 'Fussiness or irritability', 'Mild swelling']
  }, {
    type: 'paragraph',
    text: 'These reactions usually resolve within a day or two. Serious allergic reactions are extremely rare, and healthcare professionals are trained to respond appropriately should they occur.'
  }, {
    type: 'heading',
    text: 'What If My Child Misses a Vaccination?'
  }, {
    type: 'paragraph',
    text: 'Missing a vaccination does not necessarily mean your child has to start the schedule over. If your child misses an appointment:'
  }, {
    type: 'list',
    items: ['Contact your healthcare provider as soon as possible.', 'Arrange a catch-up vaccination.', 'Continue following the recommended schedule.']
  }, {
    type: 'paragraph',
    text: 'Keeping vaccinations up to date remains important even if some doses are delayed.'
  }, {
    type: 'heading',
    text: 'More Than Just Vaccinations'
  }, {
    type: 'paragraph',
    text: "Child wellness visits provide an opportunity to monitor your child's overall health and development. During these visits, healthcare providers may assess:"
  }, {
    type: 'list',
    items: ['Growth and weight', 'Height and nutrition', 'Developmental milestones', 'Hearing and vision', 'Feeding and nutrition', 'Behaviour and emotional wellbeing', 'General physical health']
  }, {
    type: 'paragraph',
    text: 'Regular wellness visits help identify potential concerns early, allowing timely intervention and support.'
  }, {
    type: 'heading',
    text: 'Tips for Parents'
  }, {
    type: 'paragraph',
    text: 'Help make vaccination visits easier by:'
  }, {
    type: 'list',
    items: ["Bringing your child's vaccination card.", 'Dressing your child in comfortable clothing.', 'Feeding your baby before the appointment if appropriate.', 'Comforting your child after the vaccination.', 'Following any aftercare advice provided by your healthcare provider.', 'Keeping future vaccination appointments.']
  }, {
    type: 'paragraph',
    text: 'Remember, a few moments of discomfort can provide years of protection.'
  }, {
    type: 'heading',
    text: 'Child Wellness Services at Maisha Medical Center'
  }, {
    type: 'paragraph',
    text: 'Our dedicated healthcare team provides comprehensive child health services, including:'
  }, {
    type: 'list',
    items: ['Routine childhood immunizations', 'Growth and development monitoring', 'General medical consultations', 'Nutrition advice', 'Child wellness examinations', 'Health education for parents', 'Preventive healthcare services', 'Referrals for specialist care when needed']
  }, {
    type: 'paragraph',
    text: 'We are committed to supporting families as children grow into healthy, confident adults.'
  }, {
    type: 'heading',
    text: 'When Should You Bring Your Child to the Clinic?'
  }, {
    type: 'paragraph',
    text: 'Schedule a visit if your child:'
  }, {
    type: 'list',
    items: ['Is due for routine vaccinations', 'Has missed any scheduled immunizations', 'Has a fever or signs of illness', 'Is not feeding well', 'Has concerns with growth or development', 'Needs a routine health check']
  }, {
    type: 'paragraph',
    text: "Regular healthcare visits are one of the best investments you can make in your child's future."
  }, {
    type: 'heading',
    text: "Protect Your Child's Future"
  }, {
    type: 'paragraph',
    text: 'Vaccinations save lives. By following the recommended immunization schedule and attending regular child wellness visits, you are giving your child the strongest possible foundation for a healthy future.'
  }, {
    type: 'paragraph',
    text: "At Maisha Medical Center, we are proud to partner with parents in providing compassionate, preventive healthcare for every child. Book your child's wellness appointment today and stay up to date with recommended vaccinations and routine health checks."
  }]
}, {
  id: 5,
  title: 'Protecting Your Vision in the Digital Age',
  excerpt: 'Screens are now part of everyday life, but prolonged screen time can contribute to eye strain and discomfort. Discover practical ways to protect your vision.',
  category: 'Eye Health & Vision Care',
  date: 'June 2026',
  readTime: '8 min read',
  author: 'Maisha Medical Center',
  image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80&w=1200',
  featured: false,
  content: [{
    type: 'paragraph',
    text: "In today's digital world, screens have become an essential part of everyday life. Whether you're working on a computer, attending online classes, scrolling through your smartphone, or watching television, your eyes are working harder than ever before."
  }, {
    type: 'paragraph',
    text: 'While digital devices have made life more convenient, prolonged screen time can contribute to eye strain, discomfort, and vision-related problems if proper care is not taken.'
  }, {
    type: 'paragraph',
    text: 'At Maisha Medical Center, we encourage everyone to prioritize their eye health through regular eye examinations, healthy habits, and early detection of vision problems.'
  }, {
    type: 'heading',
    text: 'Understanding Digital Eye Strain'
  }, {
    type: 'paragraph',
    text: 'Digital Eye Strain, also known as Computer Vision Syndrome, refers to a group of eye and vision problems caused by prolonged use of digital screens.'
  }, {
    type: 'paragraph',
    text: 'Unlike reading from paper, looking at digital screens often causes us to blink less frequently, making our eyes work harder to stay focused.'
  }, {
    type: 'paragraph',
    text: 'Although digital eye strain usually does not cause permanent damage, it can significantly affect comfort, productivity, and overall quality of life.'
  }, {
    type: 'heading',
    text: 'Common Symptoms'
  }, {
    type: 'paragraph',
    text: 'If you spend long hours using computers, smartphones, or tablets, you may experience:'
  }, {
    type: 'list',
    items: ['Tired or sore eyes', 'Blurred vision', 'Dry or watery eyes', 'Frequent headaches', 'Neck and shoulder pain', 'Difficulty focusing', 'Increased sensitivity to light', 'Burning or itchy eyes']
  }, {
    type: 'paragraph',
    text: 'These symptoms often improve after resting your eyes but may become more frequent with continued excessive screen use.'
  }, {
    type: 'heading',
    text: 'Why Regular Eye Examinations Matter'
  }, {
    type: 'paragraph',
    text: 'Many eye conditions develop gradually and may not show obvious symptoms in the early stages. Routine eye examinations can help detect:'
  }, {
    type: 'list',
    items: ['Refractive errors (short-sightedness or long-sightedness)', 'Astigmatism', 'Glaucoma', 'Cataracts', 'Diabetic eye disease', 'High blood pressure-related eye changes', 'Age-related vision problems']
  }, {
    type: 'paragraph',
    text: 'Early diagnosis allows for timely treatment and helps protect long-term vision.'
  }, {
    type: 'heading',
    text: 'The 20-20-20 Rule'
  }, {
    type: 'paragraph',
    text: 'One of the simplest ways to reduce digital eye strain is to follow the 20-20-20 Rule.'
  }, {
    type: 'paragraph',
    text: 'Every 20 minutes, look at something approximately 20 feet (6 metres) away for at least 20 seconds. This simple habit allows your eye muscles to relax and reduces fatigue during extended screen use.'
  }, {
    type: 'heading',
    text: 'Create an Eye-Friendly Workspace'
  }, {
    type: 'paragraph',
    text: 'Small adjustments to your workspace can make a big difference. Consider the following:'
  }, {
    type: 'list',
    items: ["Position your screen about an arm's length away.", 'Keep the top of your monitor slightly below eye level.', 'Reduce screen glare by adjusting lighting.', 'Increase text size if needed.', 'Maintain good posture while working.', 'Use comfortable seating with proper back support.']
  }, {
    type: 'paragraph',
    text: 'A well-designed workspace benefits both your eyes and your overall health.'
  }, {
    type: 'heading',
    text: 'Blink More Often'
  }, {
    type: 'paragraph',
    text: 'When using digital devices, people naturally blink less frequently. Blinking helps:'
  }, {
    type: 'list',
    items: ['Keep your eyes moist', 'Reduce dryness', 'Remove dust and debris', 'Improve comfort']
  }, {
    type: 'paragraph',
    text: 'Make a conscious effort to blink regularly, especially during long periods of screen use. If recommended by your healthcare provider, lubricating eye drops may also help relieve dryness.'
  }, {
    type: 'heading',
    text: 'Limit Screen Time When Possible'
  }, {
    type: 'paragraph',
    text: 'While screens are often unavoidable, taking regular breaks is important. Try to:'
  }, {
    type: 'list',
    items: ['Take short breaks every hour.', 'Avoid unnecessary screen use before bedtime.', 'Encourage children to spend time outdoors.', 'Balance screen activities with physical exercise.']
  }, {
    type: 'paragraph',
    text: 'Reducing continuous screen exposure benefits both eye health and overall wellbeing.'
  }, {
    type: 'heading',
    text: 'Protect Your Eyes Outdoors'
  }, {
    type: 'paragraph',
    text: 'Digital devices are not the only threat to healthy vision. Ultraviolet (UV) rays from the sun can also damage your eyes over time. Protect your vision by:'
  }, {
    type: 'list',
    items: ['Wearing sunglasses with UV protection.', 'Wearing a wide-brimmed hat outdoors.', 'Avoiding prolonged exposure to intense sunlight.']
  }, {
    type: 'paragraph',
    text: 'UV protection is important throughout the year, not just during summer.'
  }, {
    type: 'heading',
    text: 'Nutrition for Healthy Eyes'
  }, {
    type: 'paragraph',
    text: 'A healthy diet supports good vision. Include foods rich in:'
  }, {
    type: 'list',
    items: ['Vitamin A', 'Vitamin C', 'Vitamin E', 'Omega-3 fatty acids', 'Zinc', 'Lutein', 'Zeaxanthin']
  }, {
    type: 'subheading',
    text: 'Good choices include:'
  }, {
    type: 'list',
    items: ['Carrots', 'Spinach', 'Kale', 'Sweet potatoes', 'Citrus fruits', 'Fish such as salmon and sardines', 'Eggs', 'Nuts and seeds']
  }, {
    type: 'paragraph',
    text: 'Eating a balanced diet benefits your eyes as well as your overall health.'
  }, {
    type: 'heading',
    text: 'Eye Health for Children'
  }, {
    type: 'paragraph',
    text: 'Children today spend increasing amounts of time using phones, tablets, and computers. Parents can help by:'
  }, {
    type: 'list',
    items: ['Encouraging outdoor play', 'Limiting recreational screen time', 'Ensuring proper lighting during homework', 'Scheduling routine eye examinations', 'Watching for signs of vision problems such as squinting or sitting too close to screens']
  }, {
    type: 'paragraph',
    text: 'Healthy vision plays a vital role in learning and development.'
  }, {
    type: 'heading',
    text: 'Eye Care for People Living with Diabetes'
  }, {
    type: 'paragraph',
    text: 'People with diabetes should have regular comprehensive eye examinations. High blood sugar can damage the small blood vessels in the retina, increasing the risk of diabetic eye disease.'
  }, {
    type: 'paragraph',
    text: 'Early detection and good diabetes management can help preserve vision and prevent complications.'
  }, {
    type: 'heading',
    text: 'Optician Services at Maisha Medical Center'
  }, {
    type: 'paragraph',
    text: 'Our Optician Services are designed to help individuals and families maintain healthy vision through professional assessment and early detection. Our services include:'
  }, {
    type: 'list',
    items: ['Comprehensive eye examinations', 'Vision screening', 'Prescription assessments', 'Eye health evaluations', 'Referrals for specialist eye care when required', 'Advice on maintaining healthy vision']
  }, {
    type: 'paragraph',
    text: 'Whether you need a routine eye check or have concerns about your eyesight, our healthcare team is here to help.'
  }, {
    type: 'heading',
    text: 'When Should You Book an Eye Examination?'
  }, {
    type: 'paragraph',
    text: 'Schedule an eye examination if you:'
  }, {
    type: 'list',
    items: ['Experience blurred vision', 'Frequently suffer from headaches', 'Notice difficulty reading or driving', 'Spend long hours using digital devices', 'Have diabetes or high blood pressure', 'Have a family history of eye disease', 'Have not had an eye examination in the past two years']
  }, {
    type: 'paragraph',
    text: 'Regular eye examinations can detect problems before they affect your daily life.'
  }, {
    type: 'heading',
    text: 'Keep Your Vision Clear for the Future'
  }, {
    type: 'paragraph',
    text: 'Healthy eyesight is essential for work, education, and enjoying everyday life. By taking simple steps to reduce digital eye strain and attending regular eye examinations, you can protect your vision for years to come.'
  }, {
    type: 'paragraph',
    text: 'At Maisha Medical Center, we are committed to helping you and your family maintain healthy eyes through professional, compassionate eye care. Book your eye examination today and take the first step toward protecting your vision in the digital age.'
  }]
}];